import { Cover } from './Cover'
import { TitleBlock } from './TitleBlock'
import { Copyright } from './Copyright'
import { Terms } from './Terms'
import { CSSProperties, FC, useMemo } from 'react'
import { Button, cn } from '@wellesley/ui'
import { AppsLink } from '@wellesley/router'
import getContrast from 'helpers/color/getContrast'
import { BrandSectionSkeleton } from '../BrandSection/BrandSectionSkeleton'

type BrandSectionViewProps = {
  cover?: string
  title: string
  color: string
  serverVersion: string
  isPrivate: boolean
  description?: string
  className?: string
  readOnly?: boolean
  isLoading?: boolean
}

const isColorDark = (color: string): boolean => {
  if (!color) return false
  const contrastWithWhite = getContrast(color, '#ffffff')
  const contrastWithBlack = getContrast(color, '#000000')
  return contrastWithWhite > contrastWithBlack
}

const DEFAULT_COLOR = '#ffffff'

export const BrandSectionView: FC<BrandSectionViewProps> = ({
  cover,
  title,
  color,
  serverVersion,
  isPrivate,
  description,
  className,
  readOnly,
  isLoading,
}) => {
  const isDefaultColor = color.toLowerCase() === DEFAULT_COLOR.toLowerCase()
  const isDarkBg = useMemo(() => isColorDark(color), [color])
  const iconColor = useMemo(() => {
    if (isDefaultColor) return 'text-blue-500'
    if (isDarkBg) return 'text-white'
    return 'text-gray-900'
  }, [isDarkBg, isDefaultColor])
  const overlayColor = useMemo(
    () => (isDarkBg ? 'var(--alpha-black-30)' : 'var(--alpha-white-30)'),
    [isDarkBg],
  )

  if (isLoading) {
    return <BrandSectionSkeleton className={className} />
  }

  return (
    <div
      data-testid="branding"
      className={cn(
        'flex h-full w-[588px] flex-col justify-between bg-[--color] px-xxxl pb-xl pt-xxxl',
        className,
      )}
      style={{ '--color': color } as CSSProperties}
    >
      <div>
        <Cover
          src={cover}
          color={color}
          iconColor={iconColor}
          overlayColor={overlayColor}
        />
        <TitleBlock
          title={title}
          serverVersion={serverVersion}
          isPrivate={isPrivate}
          description={description}
          isDarkBg={isDarkBg}
        />
        {!isPrivate && (
          <div
            className={cn(
              'mt-xl flex justify-center',
              readOnly && 'pointer-events-none',
            )}
          >
            <AppsLink to="/">
              <Button
                variant="secondary"
                size="l"
                className={cn({
                  'bg-opacity-60': !isDefaultColor,
                  'text-white': isDarkBg,
                })}
              >
                Continue as guest
              </Button>
            </AppsLink>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <Copyright isDarkBg={isDarkBg} />
        <Terms isDarkBg={isDarkBg} />
      </div>
    </div>
  )
}
